import { FunctionComponent, useEffect, useState } from 'react';
import { useCoreClient, useGetPaginationState, useModal } from '@hooks';
import {
  accountState,
  amplitudeEventQueueState,
  currentOnboardingStepSelector,
  invoicesState,
  loadingState,
  notificationErrorState,
  showOnboardingFlowSelector,
} from '@states';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  AccountShopifyReview,
  AccountUsageResponseDto,
} from '@givz/core-client/dist/rest-client';
import { Icon, Link, TextStyle } from '@shopify/polaris';
import { FavoriteMajor } from '@shopify/polaris-icons';
import { useRouter } from 'next/router';
import { Routes } from '@routes';
import { showOnboardingFlowState } from 'src/states/account';
import { openUrlInNewTab } from '@utils/url';
import styled from 'styled-components';
import { FlexboxContainer } from '@components/ui/common';
import { useIsImpersonator } from '@hooks/auth';

export const ReviewStarsContainer = styled(FlexboxContainer)`
  justify-content: space-between;
  width: 180px;
  cursor: pointer;

  margin: 3rem auto;
  svg {
    fill: #f4af28;
  }

  .Polaris-Icon {
    height: 3rem;
    width: 3rem;
  }
`;

export const openReviewUrlInNewTab = () =>
  openUrlInNewTab(
    'https://apps.shopify.com/convert-discounts-to-donations/reviews#modal-show=ReviewListingModal',
  );

export const ReviewStars: FunctionComponent = () => (
  <ReviewStarsContainer onClick={openReviewUrlInNewTab}>
    <Icon source={FavoriteMajor} />
    <Icon source={FavoriteMajor} />
    <Icon source={FavoriteMajor} />
    <Icon source={FavoriteMajor} />
    <Icon source={FavoriteMajor} />
  </ReviewStarsContainer>
);

export const useFetchAccount = () => {
  const coreClient = useCoreClient();
  const [account, setAccount] = useRecoilState(accountState);
  const [, setLoading] = useRecoilState(loadingState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);

  useEffect(() => {
    const fetchAccount = async () => {
      setLoading(true);

      const { data, errors } = await coreClient().Accounts.getCurrent();

      if (errors) {
        setNotificationError(errors);
      } else {
        setAccount(data);
      }

      setLoading(false);
    };

    fetchAccount();
  }, [coreClient, setAccount, setLoading, setNotificationError]);

  return {
    account,
    setAccount,
  };
};

export const useFetchInvoices = () => {
  const [invoices, setInvoices] = useRecoilState(invoicesState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [, setLoading] = useRecoilState(loadingState);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setTotalPages,
    totalPages,
  } = useGetPaginationState();

  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      const { data, errors } = await coreClient().Invoices.search({
        page: currentPage,
        pageSize,
      });

      if (errors) {
        setNotificationError(errors);
      } else {
        setInvoices(data.invoices);
        setTotalPages(data.meta.totalPages);
      }

      setLoading(false);
    };

    fetchInvoices();
  }, [
    setLoading,
    pageSize,
    currentPage,
    setNotificationError,
    setInvoices,
    setTotalPages,
    coreClient,
  ]);

  return {
    currentPage,
    invoices,
    pageSize,
    setCurrentPage,
    setPageSize,
    totalPages,
  };
};

export const useFetchAccountUsage = () => {
  const coreClient = useCoreClient();
  const [, setLoading] = useRecoilState(loadingState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [accountUsage, setAccountUsage] = useState<AccountUsageResponseDto>();

  useEffect(() => {
    const fetchAccountUsage = async () => {
      setLoading(true);
      const { data, errors } = await coreClient().Accounts.getCurrentUsage();
      if (errors) {
        setNotificationError(errors);
      } else {
        setAccountUsage(data);
      }
      setLoading(false);
    };
    fetchAccountUsage();
  }, [coreClient, setLoading, setNotificationError]);

  const current = accountUsage?.usage.filter((usage) => usage.current);

  return {
    ...accountUsage,
    current: current && current.length > 0 ? current[0] : undefined,
  };
};

export const useFetchShouldPromptForReview = () => {
  const coreClient = useCoreClient();
  const [reviewData, setReviewData] = useState<
    AccountShopifyReview | undefined
  >();

  useEffect(() => {
    const fetchShouldPrompt = async () => {
      const { data } = await coreClient().Accounts.getCurrentShopifyAppReview();

      setReviewData(data);
    };

    fetchShouldPrompt();
  }, [coreClient]);

  return { ...reviewData };
};

export const useShowReviewModal = () => {
  const { promptForReview } = useFetchShouldPromptForReview();
  const modal = useModal();
  const [amplitudeEventQueue, setAmplitudeEventQueue] = useRecoilState(
    amplitudeEventQueueState,
  );
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (modal && promptForReview && !modalOpen) {
      modal.display(
        () => (
          <>
            <TextStyle>
              Givz is on a mission to give $1B, but we need your help. Leaving
              an app store review helps us reach more merchants like yourselves.
              Are you willing to help?
            </TextStyle>
            <ReviewStars />
            <TextStyle>
              Or,{' '}
              <Link onClick={() => openUrlInNewTab('mailto:support@givz.com')}>
                contact us with feedback
              </Link>{' '}
              any time.
            </TextStyle>
          </>
        ),
        {
          primaryAction: {
            content: 'Leave a Review',
            onAction: () => {
              setAmplitudeEventQueue([
                ...amplitudeEventQueue,
                {
                  name: 'clicked leave a review',
                },
              ]);
              openReviewUrlInNewTab();
            },
          },
          secondaryAction: {
            content: 'No thanks',
            onAction: () => {
              return;
            },
          },
          title: 'We have a favor to ask',
        },
      );

      setAmplitudeEventQueue([
        ...amplitudeEventQueue,
        {
          name: 'prompted for review',
          params: {
            version: 1,
          },
        },
      ]);
      setModalOpen(true);
    }
  }, [
    modal,
    promptForReview,
    amplitudeEventQueue,
    modalOpen,
    setAmplitudeEventQueue,
  ]);
};

export const useRedirectToOnboarding = () => {
  const router = useRouter();
  const isImpersonator = useIsImpersonator();
  const [, setShowOnboardingFlow] = useRecoilState(showOnboardingFlowState);
  const showOnboardingFlow = useRecoilValue(showOnboardingFlowSelector);
  const currentOnboardingStep = useRecoilValue(currentOnboardingStepSelector);

  useEffect(() => {
    if (showOnboardingFlow && currentOnboardingStep && !isImpersonator) {
      setShowOnboardingFlow(false);
      router.push(Routes.onboarding[currentOnboardingStep]);
    }
  }, [
    showOnboardingFlow,
    currentOnboardingStep,
    isImpersonator,
    setShowOnboardingFlow,
    router,
  ]);

  return { showOnboardingFlow };
};
